import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';
import { useMutation } from '@apollo/client';

import useSuspenseQuery from 'commons/useSuspenseQuery';
import LabeledSelectBox from 'components/consumer/LabeledSelectBox';
import IconLock2 from 'components/icons/iconLock2';
import CtaButton from 'components/essentials/Cta/CtaButton';
import StandardModal from 'components/consumer/StandardModal';
import { GET_STORE_POS_CONTEXT, GET_STORE_POS_READERS } from 'mocks/queries';
import {
  VERIFY_POS_CHARGE_ON_ORDER,
  LOG_FAILED_POS_CHARGE_ON_ORDER,
  REQUEST_POS_CHARGE_FROM_READER,
  REQUEST_POS_CHARGE_CANCEL_FROM_READER,
} from 'mocks/mutations';
import { colorGrayDark } from 'styles/layout/colors';
import ValidationError from 'components/consumer/ValidationError';
import CheckoutSubmitButton from '../../../CheckoutSubmitButton';
import ShippingInfoBlock from '../ShippingInfoBlock';

const PointOfSaleOption = ({
  cart,
  posData,
  checkout,
  showError,
  grandTotal,
  onOrderSuccess,
  placeOrderLoading,
  placeOrderWithPOS,
}) => {
  const { storeId } = posData || {};
  const { orderId, customerId, email_address, shipping } = checkout;

  const [idempotencyKey, setIdempotencyKey] = useState(uuidv4());
  const [inStoreContextId, setInStoreContextId] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [readerId, setReaderId] = useState(null);
  const [error, setError] = useState(null);
  const [modalState, setModalState] = useState({
    isOpen: false,
    isSuccess: false,
    errorMessage: null,
  });

  const { data: posReaders, loading: readersLoading } = useSuspenseQuery(
    GET_STORE_POS_READERS,
    {
      variables: {
        storeId,
      },
    }
  );

  const { data: posContext, loading: contextLoading } = useSuspenseQuery(
    GET_STORE_POS_CONTEXT,
    {
      skip: !startTime || !inStoreContextId,
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      pollInterval: 3000,
      variables: {
        startTime,
        inStoreContextId,
        email: checkout.email_address,
      },
    }
  );

  const [
    verifyPOSChargeMutation,
    { loading: verifyPOSChargeLoading },
  ] = useMutation(VERIFY_POS_CHARGE_ON_ORDER);

  const [logFailedPOSChargeMutation] = useMutation(
    LOG_FAILED_POS_CHARGE_ON_ORDER
  );

  const [
    requestChargeMutation,
    { loading: requestChargeLoading },
  ] = useMutation(REQUEST_POS_CHARGE_FROM_READER);

  const [
    requestChargeCancelMutation,
    { loading: requestChargeCancelLoading },
  ] = useMutation(REQUEST_POS_CHARGE_CANCEL_FROM_READER);

  const isLoading =
    !!startTime ||
    readersLoading ||
    contextLoading ||
    requestChargeLoading ||
    verifyPOSChargeLoading ||
    placeOrderLoading;

  useEffect(() => {
    if (
      !startTime ||
      !inStoreContextId ||
      contextLoading ||
      !posContext?.context?.inStoreContext
    ) {
      return;
    }

    const { status, transaction } = posContext.context.inStoreContext;
    switch (status) {
      case 'COMPLETE':
        setStartTime(null);
        verifyPOSChargeMutation({
          variables: {
            input: {
              orderId,
              grandTotal,
              inStoreContextId,
              email: email_address,
              zipCode: shipping.zipcode,
              customerId: parseInt(customerId, 10),
              productIds: cart.map(p => parseInt(p.product_id, 10)),
            },
          },
        })
          .then(res => {
            if (res?.data?.verifyPOSCharge?.success) {
              onOrderSuccess(orderId);
            } else {
              setStartTime(null);
              showError(
                res?.data?.verifyPOSCharge?.errors,
                'Verifying charge failed. Please try again.'
              );
            }
          })
          .catch(e => {
            setStartTime(null);
            showError(
              [{ message: e.message }],
              'Verifying charge failed. Please try again.'
            );
          });
        break;
      case 'FAILED':
        setStartTime(null);
        setInStoreContextId(null);
        logFailedPOSChargeMutation({
          variables: {
            input: {
              orderId,
              grandTotal,
              inStoreContextId,
              email: email_address,
              customerId: parseInt(customerId, 10),
            },
          },
        }).catch(() => {
          // do nothing
        });
        showError(
          [{ message: transaction?.status }],
          'Transaction failed. Please try again'
        );
        break;
      case 'CANCELLED':
        setStartTime(null);
        setInStoreContextId(null);
        break;
      case 'PENDING':
      default:
        // check timeout of 120sec
        if (Date.now() - startTime > 120000) {
          requestChargeCancelMutation({
            variables: {
              inStoreContextId,
              email: checkout.email_address,
            },
          })
            .then(res => {
              showError(
                res?.data?.requestPOSChargeCancel?.errors,
                'Purchase request timed out. Please try again'
              );
            })
            .catch(e => {
              showError([{ message: e.message }]);
            });
          setStartTime(null);
          setInStoreContextId(null);
        }
        break;
    }
    // TODO: Investigate this code and refactor to add all dependencies to this
    // hook while ensuring that it does not cause any infinite loops or
    // unnecessary duplicate API calls
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posContext, contextLoading, inStoreContextId, startTime]);

  const closeModal = () => {
    if (!requestChargeCancelLoading) {
      setModalState({
        ...modalState,
        isOpen: false,
      });
    }
  };

  const openModal = () => {
    setModalState({
      ...modalState,
      isOpen: true,
      isSuccess: false,
      errorMessage: null,
    });
  };

  const placeOrder = async () => {
    if (isLoading) {
      return;
    }

    if (!readerId) {
      setError('Select a terminal');
      return;
    }

    try {
      let order_id;
      let customer_id;
      if (!orderId) {
        const res = await placeOrderWithPOS();
        order_id = res.orderId;
        customer_id = res.customerId;
      } else {
        order_id = orderId;
        customer_id = customerId;
      }

      if (!order_id) {
        return;
      }

      const response = await requestChargeMutation({
        variables: {
          input: {
            email: checkout.email_address,
            firstName: shipping.first_name,
            lastName: shipping.last_name,
            phoneNumber: shipping.phone_number,
            amount: grandTotal,
            orderId: order_id,
            customerId: customer_id,
            idempotencyKey,
            readerId,
          },
        },
      });

      if (response.data?.requestPOSCharge?.inStoreContext?.id) {
        // setting these vars will start the polling
        setStartTime(Date.now());
        setInStoreContextId(response.data.requestPOSCharge.inStoreContext.id);
        setIdempotencyKey(uuidv4());
      } else {
        // TODO: don't set idempotency key for network error
        setStartTime(null);
        setIdempotencyKey(uuidv4());
        showError(
          response.data?.requestPOSCharge?.errors,
          'Unable to request charge on the card. Please try again.'
        );
      }
    } catch (e) {
      showError([{ message: e.message }]);
    }
  };

  const requestCancel = async () => {
    try {
      setModalState({
        ...modalState,
        isSuccess: false,
        errorMessage: null,
      });

      if (!inStoreContextId) {
        return;
      }

      const response = await requestChargeCancelMutation({
        variables: {
          inStoreContextId,
          email: checkout.email_address,
        },
      });

      if (
        response.data?.requestPOSChargeCancel?.inStoreContext?.status ===
        'CANCELLED'
      ) {
        // setting these vars will start the polling
        setModalState({
          ...modalState,
          isSuccess: true,
        });
        setStartTime(null);
        setInStoreContextId(null);
      } else {
        setModalState({
          ...modalState,
          errorMessage:
            response.data?.requestPOSChargeCancel?.errors?.[0]?.message ||
            'Failed to cancel the transaction',
        });
      }
    } catch (e) {
      setModalState({
        ...modalState,
        errorMessage: e.message || 'Failed to cancel the transaction',
      });
    }
  };

  return (
    <div>
      <div className="text-sm text-gray mt-6 mx-0 mb-0 font-bold [&_svg_path]:fill-gray">
        <IconLock2 /> <span className="ml-2">Secure and encrypted</span>
      </div>

      <div className="mt-8 mb-4">
        <span className="text-base font-bold">Terminal</span>
        <LabeledSelectBox
          errorMessage={!readerId && error}
          label="Select your terminal"
          options={
            posReaders?.storeReaders?.readers?.length > 0
              ? posReaders.storeReaders.readers.map(r => ({
                  value: r.id,
                  label: r.name,
                }))
              : []
          }
          value={readerId}
          onChange={evt => {
            if (!isLoading) {
              setReaderId(evt.target.value);
              setIdempotencyKey(uuidv4());
            }
          }}
        />
      </div>

      <div>{checkout && <ShippingInfoBlock checkout={checkout} />}</div>

      <div className="mt-8">
        <div className="flex justify-end my-[30px]">
          <CheckoutSubmitButton
            dataXc="place-order-pos-btn"
            disabled={isLoading}
            onClick={placeOrder}
            isLoading={isLoading}
          >
            Submit order
          </CheckoutSubmitButton>
        </div>
        {!!startTime && (
          <div className="flex justify-end my-[30px]">
            <CheckoutSubmitButton
              dataXc="place-order-pos-btn"
              disabled={requestChargeCancelLoading}
              onClick={openModal}
              isLoading={requestChargeCancelLoading}
            >
              Cancel transaction
            </CheckoutSubmitButton>
          </div>
        )}
      </div>
      <StandardModal
        title=""
        theme=""
        onClose={closeModal}
        isOpen={modalState.isOpen}
        closeIconColor={colorGrayDark}
      >
        <div>
          <h2 className="text-2xl font-bold text-gray-dark mt-0 mx-0 mb-3">
            Cancel Charge
          </h2>
          <div className="text-gray-dark mt-[1.2rem] mx-0 mb-2">
            {modalState.errorMessage ? (
              <ValidationError block>{modalState.errorMessage}</ValidationError>
            ) : modalState.isSuccess ? (
              <div className="text-brand text-[.8rem] px-0 py-0.5">
                Cancelled successfully.
              </div>
            ) : (
              <div className="text-base font-bold">
                Are you sure you want to cancel the transaction ?
              </div>
            )}
          </div>
          <div className="text-right [&_button]:ml-10">
            {modalState.isSuccess ? (
              <CtaButton
                color="brand"
                styling="solid-button"
                onClick={closeModal}
              >
                <span className="items-center inline-flex hover:color-white">
                  Close
                </span>
              </CtaButton>
            ) : (
              <>
                <CtaButton
                  color="brand"
                  styling="link"
                  hasArrow={false}
                  onClick={closeModal}
                >
                  No
                </CtaButton>
                <CtaButton
                  color="brand"
                  styling="solid-button"
                  onClick={requestCancel}
                  isLoading={requestChargeCancelLoading}
                >
                  Yes
                </CtaButton>
              </>
            )}
          </div>
        </div>
      </StandardModal>
    </div>
  );
};

PointOfSaleOption.propTypes = {
  posData: PropTypes.shape({
    storeId: PropTypes.string,
  }),
  cart: PropTypes.arrayOf(
    PropTypes.shape({
      product_id: PropTypes.number,
    })
  ),
  grandTotal: PropTypes.number,
  onOrderSuccess: PropTypes.func,
  checkout: PropTypes.shape({
    orderId: PropTypes.string,
    customerId: PropTypes.string,
    email_address: PropTypes.string.isRequired,
    shipping: PropTypes.shape({
      first_name: PropTypes.string,
      last_name: PropTypes.string,
      zipcode: PropTypes.string,
      phone_number: PropTypes.string,
    }),
  }),
  showError: PropTypes.func,
  placeOrderLoading: PropTypes.bool,
  placeOrderWithPOS: PropTypes.func,
};

export default PointOfSaleOption;
